import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { crm } from './crmApi';
import type { CrmAuth, CrmLead, CrmMe, PollAnswer } from './crmApi';
import { HolidayWarning, Modal, fmtPhone, fmtWhen, fromLocalInput, holidayProblem, inputCls, labelCls, offHolidays, purpleBg, toLocalInput } from './ui';

// ── The poll's status ────────────────────────────────────────
// After Brochure Sent → Poll Sent: Interested (waiting for their payment,
// then Converted), Not Interested (with why) or No Response (no reply; the
// lead stays with them). It can change later if they reply.
// Mirrors the app's crm_poll_sheet.dart.

const ANSWERS: { key: PollAnswer; icon: string; hint: string; on: string }[] = [
  { key: 'Interested', icon: '✅', hint: 'They want to join. Mark them Converted when they pay.', on: '#047857' },
  { key: 'Not Interested', icon: '❌', hint: 'They said no. Pick why.', on: '#b91c1c' },
  { key: 'No Response', icon: '🔕', hint: 'No reply yet. The lead stays with you.', on: '#475569' },
];

// Quick picks for when an Interested lead will pay (India time; a holiday
// moves to the next working day)
function payQuick(me: CrmMe): [string, string][] {
  const now = Date.now();
  const ist = new Date(now + 330 * 60000);
  const day = (n: number, hh: number) => {
    const d = new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate() + n, hh, 0) - 330 * 60000);
    return offHolidays(me, toLocalInput(d));
  };
  return [['In 2 hours', toLocalInput(new Date(now + 2 * 3600e3))], ['Next day 11 AM', day(1, 11)], ['In 3 days', day(3, 11)]];
}

const PollModal: React.FC<{ auth: CrmAuth; lead: CrmLead; me: CrmMe; initial?: PollAnswer; onClose: () => void; onSaved: (lead: CrmLead) => void }> = ({ auth, lead, me, initial, onClose, onSaved }) => {
  const [answer, setAnswer] = useState<PollAnswer | ''>(initial || '');
  const [payAt, setPayAt] = useState('');
  const [reason, setReason] = useState('');
  const [other, setOther] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const finalReason = reason === 'Other' ? other.trim() : reason;
  const reasons = me.lists.notInterestedReasons.includes('Other') ? me.lists.notInterestedReasons : [...me.lists.notInterestedReasons, 'Other'];

  const save = async () => {
    if (!answer) { toast.error('Pick the poll status'); return; }
    if (answer === 'Not Interested' && !finalReason) { toast.error('Say why they are not interested'); return; }
    if (answer === 'Interested' && payAt && new Date(fromLocalInput(payAt)).getTime() < Date.now() - 5 * 60000) { toast.error('The payment date is in the past'); return; }
    if (answer === 'Interested' && payAt && holidayProblem(me, payAt)) { toast.error(holidayProblem(me, payAt)); return; }
    setSaving(true);
    try {
      const r = await crm.pollStatus(auth, {
        leadId: lead.id, status: answer, note: note.trim(),
        ...(answer === 'Not Interested' ? { reason: finalReason } : {}),
        ...(answer === 'Interested' && payAt ? { nextAt: fromLocalInput(payAt) } : {}),
      });
      toast.success(`${lead.name || fmtPhone(lead.phone)}: ${answer}${answer === 'Interested' ? (payAt ? `, pays ${fmtWhen(r.lead.nextAt)}` : ', waiting for payment') : ''}`);
      onSaved(r.lead);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Poll status: ${lead.name || fmtPhone(lead.phone)}`} sub={lead.pollSentAt ? `Poll sent ${fmtWhen(lead.pollSentAt)} · now ${lead.stage}` : `Now ${lead.stage}`} onClose={onClose}>
      <div className="space-y-4">
        <div className="grid gap-2" role="radiogroup" aria-label="Poll status">
          {ANSWERS.map(a => {
            const on = answer === a.key;
            return (
              <button key={a.key} type="button" role="radio" aria-checked={on} onClick={() => setAnswer(a.key)}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border-2 text-left transition-all"
                style={on ? { borderColor: a.on, background: `${a.on}10` } : { borderColor: '#e5e7eb', background: '#fff' }}>
                <span className="text-2xl" aria-hidden>{a.icon}</span>
                <span className="flex-1 min-w-0">
                  <span className="block font-bold text-gray-900">{a.key}</span>
                  <span className="block text-xs text-gray-500">{a.hint}</span>
                </span>
                <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 ${on ? 'border-[6px]' : ''}`} style={{ borderColor: on ? a.on : '#d1d5db' }} />
              </button>
            );
          })}
        </div>

        {answer === 'Interested' && (
          <div>
            <label className={labelCls} htmlFor="poll-pay">When will they pay? (optional)</label>
            <input id="poll-pay" type="datetime-local" value={payAt} onChange={e => setPayAt(e.target.value)} className={inputCls} />
            <div className="flex flex-wrap gap-1.5 mt-2">
              <button type="button" onClick={() => setPayAt('')} className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border ${!payAt ? 'border-purple-300 text-purple-700 bg-purple-50' : 'border-gray-200 text-gray-600'}`}>Not sure</button>
              {payQuick(me).map(([label, v]) => (
                <button key={label} type="button" onClick={() => setPayAt(v)} className="px-2.5 py-1 rounded-lg text-[11px] font-bold border border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-700">{label}</button>
              ))}
            </div>
            <HolidayWarning me={me} value={payAt} />
            <p className="text-[11px] text-gray-400 mt-1.5">With a date you get a reminder then. When they pay, open the lead and press “They paid”.</p>
          </div>
        )}

        {answer === 'Not Interested' && (
          <div className="space-y-2">
            <label className={labelCls} htmlFor="poll-reason">Why not interested?</label>
            <select id="poll-reason" value={reason} onChange={e => setReason(e.target.value)} className={inputCls}>
              <option value="">— Pick a reason —</option>
              {reasons.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            {reason === 'Other' && <input value={other} onChange={e => setOther(e.target.value)} placeholder="Type the reason" className={inputCls} aria-label="The reason" />}
          </div>
        )}

        {answer && (
          <div>
            <label className={labelCls} htmlFor="poll-note">Note (optional)</label>
            <textarea id="poll-note" rows={2} value={note} onChange={e => setNote(e.target.value)} className={`${inputCls} resize-none`} placeholder="What they said" />
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={save} disabled={saving || !answer} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={purpleBg}>
            {saving ? 'Saving…' : 'Save poll status'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PollModal;
